import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { InvoiceTemplate, SeededInvoiceTemplate } from "../models/InvoiceTemplate";
import { apiStore } from "../stores/api.store";

const QUERY_KEY = "invoice-templates";

/**
 * All invoice templates for the organisation. The first call seeds the two
 * locked standard templates server-side, so the list is never empty.
 */
export function useInvoiceTemplates(includeArchived = false, enabled = true) {
	const { data, ...rest } = useQuery(
		[QUERY_KEY, { includeArchived }],
		() =>
			apiStore
				.get<{ templates: InvoiceTemplate[] }>({
					path: `/invoice-template`,
					query: includeArchived ? { includeArchived: true } : {},
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{ enabled, staleTime: 30_000 }
	);

	return { templates: data?.templates ?? [], ...rest };
}

/** The locked standard templates, in the order they are listed everywhere. */
export const STANDARD_TEMPLATE_ORDER: SeededInvoiceTemplate[] = ["DETAILED", "DETAILED_GROUPED"];

/** Standard templates first (per uppdrag, per kund), then the org's own by name. */
export const compareInvoiceTemplates = (a: InvoiceTemplate, b: InvoiceTemplate) => {
	const rank = (t: InvoiceTemplate) =>
		t.seeded ? STANDARD_TEMPLATE_ORDER.indexOf(t.seeded) : STANDARD_TEMPLATE_ORDER.length;

	return rank(a) - rank(b) || a.name.localeCompare(b.name, "sv");
};

/** Non-archived templates, standard ones first — for assignment selects. */
export function useAssignableInvoiceTemplates(enabled = true) {
	const { templates, ...rest } = useInvoiceTemplates(false, enabled);

	const assignable = useMemo(
		() => templates.filter((t) => !t.archived).sort(compareInvoiceTemplates),
		[templates]
	);

	return { templates: assignable, ...rest };
}
